<script lang="ts" setup>
import { isEmpty } from '@tools/helpers';
import { computed, nextTick, ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    ariaLabel?: string;
    path?: string;
    value?: string | number | null;
    name?: string;
    placeholder?: string;
    readonly?: boolean;
    disabled?: boolean;
    minValue?: number;
    maxValue?: number;
    onFocus?: boolean;
    autocomplete?: boolean;
    hasError?: boolean;
    id?: string;
    /** Verbergt de op-/neerpijltjes van het getalveld; typen blijft gewoon werken. */
    hideSpinButtons?: boolean;
  }>(),
  {
    path: undefined,
    value: undefined,
    readonly: false,
    disabled: false,
    autocomplete: true,
    hasError: false,
    hideSpinButtons: false,
  },
);

const emit = defineEmits<{
  updateField: [{ path: string | undefined; value: number | null }];
  'update:value': [number | null];
  blur: [];
}>();

const val = ref(props.value);
watch(
  () => props.value,
  (v) => (val.value = v),
);

const root = ref();

const computedVal = computed(() => {
  if (val.value === null) return null;
  const numVal = typeof val.value === 'string' ? parseFloat(val.value) : val.value;

  if (numVal === undefined) return val.value;
  if (Number.isNaN(numVal)) return val.value;
  if (numVal === undefined) return val.value;

  if (props.minValue !== undefined && numVal < props.minValue) return props.minValue;
  if (props.maxValue !== undefined && numVal > props.maxValue) return props.maxValue;

  return val.value;
});

/**
 * Readonly toont een streepje als er niets is ingevuld. Nul is wél een waarde, dus alleen echt lege
 * waarden vallen terug op het streepje; isEmpty kan hier niet, want die rekent 0 als leeg.
 */
const readonlyWeergave = computed(() => {
  const waarde = computedVal.value;
  return waarde === null || waarde === undefined || waarde === '' ? '-' : waarde;
});

watch(
  () => props.onFocus,
  () => {
    nextTick(() => root.value.focus());
  },
);

const onBlur = () => emit('blur');

function handleInput(value: number | string) {
  val.value = value;
  emit('updateField', { path: props.path, value: value !== '' ? Number(value) : null });
  emit('update:value', value !== '' ? Number(value) : null);
}

function checkIfNumber(event: KeyboardEvent) {
  if (/^[0-9]$/.test(event.key)) return;
  if (event.key === '-' && props.minValue !== undefined && props.minValue < 0) return;
  event.preventDefault();
}
</script>

<template>
  <div v-if="!readonly" :class="['relative flex flex-1', { filled: val }]">
    <input
      :aria-label="props.ariaLabel"
      :class="[
        'styled-input',
        'group-[&>*:not(:first-child)]/input-group:rounded-s-none! group-[&>*:not(:first-child)]/input-group:border-s-0! group-[&>*:not(:last-child)]/input-group:rounded-e-none! group-[&>*:not(:last-child)]/input-group:border-e-0!',
        {
          filled: !isEmpty(computedVal),
          'border-red-500!': props.hasError,
          '[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:hidden':
            props.hideSpinButtons,
        },
      ]"
      type="number"
      :value="computedVal"
      :placeholder="placeholder"
      :disabled="disabled"
      :min="minValue"
      :max="maxValue"
      @keypress="checkIfNumber"
      @blur="onBlur"
      data-testid="number-field-input"
      ref="root"
      :name="name"
      :autocomplete="autocomplete ? 'on' : 'off'"
      :id="id"
      @input="({ target }) => handleInput((target as HTMLInputElement).value)"
    />
  </div>
  <div v-else class="filled group-[&]/float-label:h-11 group-[&]/float-label:content-center">
    {{ readonlyWeergave }}
  </div>
</template>
